import { Size } from '@ps/web-core-enums';
import type { Size as ModalSize } from '@ps/web-core-store';
import { getScrollingElem } from '@ps/web-core-utils';
import { useBodyScrollLock, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import styles from './BaseModal.scss';

interface Props {
  className?: string;
  overlayClassName?: string;
  bodyOpenClassName?: string;
  enableTransparentBackground?: boolean;
  isOpen: boolean;
  size: ModalSize;
  hidden?: boolean;
  showAnimation?: boolean;
  fullScreen?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  onRequestClose?(): void;
}

const scrollingElement = getScrollingElem();

const BaseModal: React.FC<React.PropsWithChildren<Props>> = ({
  showAnimation = true,
  enableTransparentBackground = false,
  isOpen,
  className,
  overlayClassName,
  size,
  hidden,
  onRequestClose,
  children,
  fullScreen,
  closeOnOverlayClick = false,
  closeOnEsc = false
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [overlay, setOverlay] = useState<HTMLDivElement | null>(null);
  const prevScrollTop = useRef(0);
  const { greaterThan } = useScreenSize();
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isMobile = useSelector(select.device.isMobile);

  const overlayRef = (ref: HTMLDivElement) => setOverlay(ref);

  const handleClose = useCallback(() => {
    if (!greaterThan(Size.XXSmall) && scrollTop && scrollingElement) {
      scrollingElement.scrollTop = scrollTop;
      setScrollTop(0);
    }
  }, [greaterThan, scrollTop]);

  useBodyScrollLock(isOpen, overlay);

  if (isOpen && !prevScrollTop.current && scrollingElement) {
    const scrollingElementScrollTop = scrollingElement.scrollTop;
    if (scrollingElementScrollTop) {
      setScrollTop(scrollingElementScrollTop);
      prevScrollTop.current = scrollingElementScrollTop;
    }
  }

  return useMemo(
    () => (
      <ReactModal
        bodyOpenClassName={styles.bodyOpen}
        className={classNames(
          styles.modal,
          styles[size as keyof typeof styles],
          {
            [styles.hidden]: hidden,
            [styles.modalTransition]: showAnimation
          },
          className
        )}
        isOpen={isOpen}
        onAfterClose={handleClose}
        onRequestClose={onRequestClose}
        overlayClassName={classNames(
          {
            [styles.transparentBackground]: (isMobile && fullScreen) || enableTransparentBackground
          },
          styles.overlay,
          { [styles.inactive]: hidden, [styles.desktopEmbedded]: isDesktopEmbedded },
          styles.overlayTransition,
          overlayClassName
        )}
        shouldCloseOnOverlayClick={closeOnOverlayClick}
        shouldCloseOnEsc={closeOnEsc}
        overlayRef={overlayRef}
      >
        {children}
      </ReactModal>
    ),
    [
      size,
      hidden,
      showAnimation,
      enableTransparentBackground,
      className,
      isOpen,
      handleClose,
      onRequestClose,
      isMobile,
      fullScreen,
      isDesktopEmbedded,
      overlayClassName,
      closeOnOverlayClick,
      closeOnEsc,
      children
    ]
  );
};

export default BaseModal;
